/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 */

import React, { useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { Icon, ICON_SIZE } from "../../../../ui/components/Icon";
import { IDocNavItem } from "../../DocumentationTypes";

export interface IDocumentationNavProps {
	item: IDocNavItem;
	selected: string | null;
	setSelected: (path: string) => void;
	level: number;
}

export const DocumentationNav: React.FC<IDocumentationNavProps> = ({
	item,
	selected,
	setSelected,
	level
}) => {
	const baseClass = BEM("edt-documentation-nav-item");
	const [isExpanded, setIsExpanded] = useState(true);

	const isSelected = selected === item.path;
	const hasChildren = item.children && item.children.length > 0;

	const handleClick = () => {
		if (item.isFolder) {
			setIsExpanded(!isExpanded);
		} else {
			setSelected(item.path);
		}
	};

	if (isSelected) {
		baseClass.mod("selected");
	}

	if (item.isFolder) {
		baseClass.mod("folder");
	}

	if (item.isFolder && isExpanded) {
		baseClass.mod("expanded");
	}

	return (
		<div className={baseClass.toClassName()}>
			<div
				className={baseClass.element("content").toClassName()}
				onClick={handleClick}
				style={{ paddingLeft: `${level * 16 + 12}px` }}
			>
				{item.isFolder && (
					<Icon
						name="mdi/chevron-right"
						size={ICON_SIZE.XSMALL}
						className={baseClass.element("icon").toClassName()}
					/>
				)}
				{!item.isFolder && (
					<Icon
						name="mdi/file-document-outline"
						size={ICON_SIZE.XSMALL}
						className={baseClass.element("icon").toClassName()}
					/>
				)}
				<span className={baseClass.element("title").toClassName()}>
					{item.title}
				</span>
			</div>
			{hasChildren && isExpanded && (
				<div className={baseClass.element("children").toClassName()}>
					{item.children?.map((child) => (
						<DocumentationNav
							key={child.path}
							item={child}
							selected={selected}
							setSelected={setSelected}
							level={level + 1}
						/>
					))}
				</div>
			)}
		</div>
	);
};
